/*
 * landing-page
 * ==================================================================
 * Forked from assets/multi-discount.js (which powers the live lp25x pages — do
 * NOT edit that one). Same behaviour, renamed custom elements so the two coexist:
 *   virtual-cart -> lp-cart   |   bundle-item -> lp-bundle-item   |   upsell-item -> lp-upsell-item
 *
 * Single file (no bundler), organised by concern:
 *   1. Data layer  — lpDiscountApi (Hy global) + thin adapters (getTotals, buildMilestones,
 *                    discountedUnitPrice) over the PURE layer in assets/lp-pricing.js
 *   2. UI widgets  — LpTracker (DOM/styling for the footer)
 *   3. Elements    — LpVariantItem (base) -> LpBundleItem / LpUpsellItem, and LpCart
 *
 * The pure, DOM-free pricing math lives in assets/lp-pricing.js (window.LpPricing),
 * loaded before this file — so it's unit-testable in Node with a fake calculator. This
 * file only reads/writes the DOM and injects the Hy calculator into those pure functions.
 *
 * Preserved: virtual multi-select, discount totals via the external Hy app, add-all
 * with `_lp_exclusive`, clear-then-add, redirect to /checkout, and the data-* paint
 * contract. Fallback: when the Hy global is absent (dev theme), totals sum the line
 * prices (no discount).
 */

/* ════════════════════════ 1. Data layer ════════════════════════ */

// Pure pricing functions (no DOM/globals) — see assets/lp-pricing.js. Loaded first, so
// window.LpPricing is defined by the time this script runs.
const {
  formatMoney,
  selectionCount,
  isEarned,
  cartTotals,
  lineDiscountedPrice,
  parseDiscountConfig,
} = window.LpPricing;

// The live discount global injected by the Hy "zoeva-discounts" app embed.
function lpDiscountApi() {
  return (
    window.Hy && window.Hy.ZoevaDiscounts && window.Hy.ZoevaDiscounts.landingPageExclusiveDiscount
  );
}

// Original/discounted totals (cents). Adapter: injects the Hy app into the pure cartTotals
// (which sums line prices when the app is absent).
function getTotals(cart, basePrice) {
  return cartTotals(cart, basePrice, lpDiscountApi());
}

// Derive the ordered milestone ladder for THIS page's products. Adapter: reads the Hy
// config element + the page's products from the DOM, then hands plain data to the pure
// parseDiscountConfig (which builds the base / scalePrice / 100%-off-gift ladder).
function buildMilestones(host, currency, debug) {
  const api = lpDiscountApi();
  const el = api && api.functionId && document.getElementById(api.functionId);
  if (!el) return [];
  let cfg;
  try {
    cfg = JSON.parse(el.textContent);
  } catch (e) {
    return [];
  }
  if (debug) {
    console.log('[lp-cart] discount config source = zoeva-discounts app', {
      functionId: api.functionId,
      sourceElement: el,
      rawConfig: cfg,
    });
  }

  const items = Array.from(host.querySelectorAll('lp-bundle-item'));
  const pageProducts = new Set(items.map((i) => i.getAttribute('product-id')));
  const prices = items.map((i) => Number(i.getAttribute('variant-price')));
  return parseDiscountConfig(cfg, pageProducts, prices, currency);
}

/* ════════════════════════ 2. UI widgets ════════════════════════ */

// Savings tracker: segmented rail + labels + headline. update() returns
// advancement info so the cart can fire a celebration burst.
class LpTracker {
  constructor(host) {
    this.host = host;
    this.prevCurrentIdx = -1;
    this.prevCount = 0;
    this.armed = false;
  }

  init(milestones, format) {
    this.milestones = milestones || [];
    this.format = format;
    this.receiptEl = this.host.querySelector('[data-receipt]');
    if (this.receiptEl) {
      this.rBasket = this.receiptEl.querySelector('[data-r-basket]');
      this.rSave = this.receiptEl.querySelector('[data-r-save]');
      this.rSaveAmt = this.receiptEl.querySelector('[data-r-save-amt]');
    }
    this.toolsEl = this.host.querySelector('[data-fan-tools]');
    this.fanDeck = this.host.querySelector('[data-fan-deck]');
    this.fanRow = this.host.querySelector('[data-fan-row]');
    // Clone templates for the coins (markup + localized strings authored in the footer snippet).
    this.tplCoin = this.host.querySelector('[data-tpl-coin]');
    this.tplGhost = this.host.querySelector('[data-tpl-ghost]');
    this.tplPlus = this.host.querySelector('[data-tpl-plus]');
    this.tplTool = this.host.querySelector('[data-tpl-tool]');
    if (this.fanRow && !this._fanBound) {
      this._fanBound = true;
      // Clicking a filled coin deselects that shade. Synthetic input event (isTrusted:false),
      // so it isn't treated as a "tap" for the card-notification; it re-renders the deck.
      this.fanRow.addEventListener('click', (e) => {
        const btn = e.target.closest('.lpv2-fan__mini[data-variant]');
        if (!btn) return;
        const vid = Number(btn.getAttribute('data-variant'));
        const card = document.querySelector('lp-bundle-item[variant-id="' + vid + '"]');
        const input = card && card.querySelector('input.lpv2-bundle-input');
        if (input && input.checked) {
          input.checked = false;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
      // Re-evaluate the clip line + text fit when the viewport width changes.
      window.addEventListener('resize', () => {
        this.updateFanClip();
        this.fitGhostText();
      });
      // Re-fit once the web font loads (its metrics differ from the fallback).
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.fitGhostText());
      }
    }
    this.statusEl = this.host.querySelector('[data-track-status]');
    this.trackEl = this.host.querySelector('[data-track]');

    this.overrides = {};
    const ovEl = this.host.querySelector('[data-lp-milestone-labels]');
    if (ovEl) {
      try {
        this.overrides = JSON.parse(ovEl.textContent) || {};
      } catch (e) {}
    }

    this.update(0, 0, 0);
    this.renderFan([]); // initial empty state — ghost tiles + trailing +
  }

  // Make the progress status a polite live region only AFTER the initial load
  // cascade, so the pre-selected state isn't announced — only later changes.
  arm() {
    this.armed = true;
    if (this.statusEl) {
      this.statusEl.setAttribute('aria-live', 'polite');
      this.statusEl.setAttribute('aria-atomic', 'true');
    }
  }

  // Compute the reached-tier index from the milestone ladder and refresh the readouts.
  // Returns { idx, advanced } so the cart can fire a celebration + card-notification on a new tier.
  // original/discounted are whole-cart totals (cents); items is the basket line count.
  update(count, original, discounted, items) {
    const ms = this.milestones;
    if (!ms || !ms.length) return { advanced: false, idx: -1 };
    let currentIdx = -1;
    for (let i = 0; i < ms.length; i += 1) {
      if (count >= ms[i].quantity) currentIdx = i;
    }
    this.renderReceipt(count, original, discounted, items);
    this.updateCompanions(count);
    this.updateStatus(count);
    this.updateDealCta(currentIdx, Math.max((original || 0) - (discounted || 0), 0));

    const advanced = currentIdx >= 0 && currentIdx > this.prevCurrentIdx;
    this.prevCurrentIdx = currentIdx;
    this.prevCount = count;
    return { idx: currentIdx, advanced };
  }

  // Basket receipt chip: "Basket: {n} items" + a "Save {amount} ({pct}% off)" clause when the Hy
  // discount applies. Words + {count}/{amount}/{pct} tokens are authored in the footer snippet;
  // here we only fill textContent (pluralised one/other) and set the deal tier for the shimmer.
  renderReceipt(count, original, discounted, items) {
    const el = this.receiptEl;
    if (!el) return;
    const saved = Math.max((original || 0) - (discounted || 0), 0);
    const pct = original > 0 ? Math.round((saved * 100) / original) : 0;

    if (count < 1) {
      el.hidden = true;
      el.removeAttribute('data-tier');
      return;
    }
    el.hidden = false;

    const n = items != null ? items : count;
    const d = el.dataset;
    if (this.rBasket) {
      const tpl = n === 1 ? d.basketOne || '' : d.basketOther || '';
      this.rBasket.textContent = tpl.replace('{count}', n);
    }

    const showSave = saved > 0;
    if (this.rSave) this.rSave.hidden = !showSave;
    if (showSave && this.rSaveAmt) {
      this.rSaveAmt.textContent = (d.saveTpl || '')
        .replace('{amount}', formatMoney(saved, this.format))
        .replace('{pct}', pct);
    }

    // Deal tier from the milestone ladder (shade count): top = best, one below = great.
    const ms = this.milestones;
    const lastQ = ms.length ? ms[ms.length - 1].quantity : Infinity;
    const secondQ = ms.length > 1 ? ms[ms.length - 2].quantity : Infinity;
    let tier = '';
    if (showSave) {
      if (count >= lastQ) tier = 'best';
      else if (count >= secondQ) tier = 'great';
    }
    if (tier) el.setAttribute('data-tier', tier);
    else el.removeAttribute('data-tier');
  }

  // Deal-tier flag on the checkout button: the top milestone tier = "best", the one below it
  // = "great" (read from the ladder, not hard-coded counts), and only with a real saving.
  // Toggles .is-deal + data-tier; CSS reveals the matching label + shimmer.
  updateDealCta(currentIdx, saved) {
    const btn = this.host.querySelector('[checkout-button]');
    if (!btn) return;
    const last = this.milestones.length - 1;
    let tier = '';
    if (saved > 0 && currentIdx >= 0) {
      if (currentIdx === last) tier = 'best';
      else if (currentIdx === last - 1) tier = 'great';
    }
    btn.classList.toggle('is-deal', !!tier);
    if (tier) btn.setAttribute('data-tier', tier);
    else btn.removeAttribute('data-tier');
  }

  // Free-tool gift coins docked at the right of the fan-deck. Each gift upsell shows a coin
  // whatever the state: FREE once its shade threshold is reached, ADDED if the shopper paid
  // for it, else a greyed ghost.
  updateCompanions(count) {
    const wrap = this.toolsEl;
    if (!wrap || !this.tplTool) return;
    const hintTpl = wrap.dataset.toolHint || '';
    const frag = document.createDocumentFragment();
    this.host.querySelectorAll('lp-upsell-item').forEach((el) => {
      const trigger = Number(el.getAttribute('trigger-quantity')) || 0;
      if (!trigger) return; // only gifts (with a threshold) show as tool coins
      const name = el.getAttribute('data-name') || el.getAttribute('product-title') || '';
      const img = el.getAttribute('data-coin-url') || '';
      const input = el.querySelector('input');
      const free = count >= trigger;
      const inCart = input ? input.checked : false;

      const node = this.tplTool.content.firstElementChild.cloneNode(true);
      node.title = hintTpl.replace('{product}', name).replace('{count}', trigger);
      const mini = node.querySelector('.lpv2-fan__mini--gift');
      if (mini) mini.dataset.st = free ? 'free' : inCart ? 'added' : 'ghost';
      const imgEl = node.querySelector('.lpv2-fan__img');
      if (imgEl && img) imgEl.style.backgroundImage = "url('" + img + "')";
      const freeBadge = node.querySelector('[data-badge="free"]');
      const addedBadge = node.querySelector('[data-badge="added"]');
      if (freeBadge) freeBadge.hidden = !free;
      if (addedBadge) addedBadge.hidden = free || !inCart;
      frag.appendChild(node);
    });
    wrap.replaceChildren(frag);
  }

  // Selected-shade thumbnails in the footer — one per selected shade (type 'default'),
  // in cart order. Minimal render; the final swatch UI is still in design. Items without an
  // image are skipped. The data (imageUrl + handle) is plumbed via readData on the cards.
  // Build the fan-deck: one overlapping coin per selected shade (packshot + green ✓), ghost
  // tiles (minicard art) for the upcoming tiers up to at least 4 slots — the next one pulsing —
  // and a trailing +. Coins are removable (see the click handler in init).
  renderFan(lines) {
    const row = this.fanRow;
    if (!row || !this.tplCoin) return;
    const removeTpl = row.dataset.removeLabel || '';
    const shades = (lines || []).filter((l) => l.type === 'default');
    const slots = Math.max(4, shades.length);
    const frag = document.createDocumentFragment();
    let firstGhost = true;
    for (let i = 0; i < slots; i += 1) {
      const s = shades[i];
      if (s) {
        const node = this.tplCoin.content.firstElementChild.cloneNode(true);
        node.dataset.variant = s.variantId;
        node.setAttribute('aria-label', removeTpl.replace('{shade}', s.title));
        const imgEl = node.querySelector('.lpv2-fan__img');
        if (imgEl && s.imageUrl) imgEl.style.backgroundImage = "url('" + s.imageUrl + "')";
        frag.appendChild(node);
      } else if (this.tplGhost) {
        const node = this.tplGhost.content.firstElementChild.cloneNode(true);
        if (firstGhost) node.classList.add('lpv2-fan__mini--next');
        const tier = this.milestones[i];
        const ov = (tier && this.overrides[tier.quantity]) || {};
        if (ov.coinImage) {
          // Image variant: fill the tile with the picked art; text is the fallback only.
          node.classList.add('lpv2-fan__mini--img');
          const img = node.querySelector('[data-ghost-img]');
          if (img) {
            img.src = ov.coinImage;
            img.alt = ov.coinImageAlt || ov.coinHeadline || '';
          }
        } else {
          const hd = node.querySelector('[data-ghost-hd]');
          const sub = node.querySelector('[data-ghost-sub]');
          if (hd) hd.textContent = ov.coinHeadline || '';
          if (sub) sub.textContent = ov.coinReward || '';
        }
        frag.appendChild(node);
        firstGhost = false;
      }
    }
    if (this.tplPlus) frag.appendChild(this.tplPlus.content.firstElementChild.cloneNode(true));
    row.replaceChildren(frag);
    this.fitGhostText();
    this.updateFanClip();
  }

  // Shrink the ghost-tile text uniformly until the longest one fits its coin — so translated
  // strings (e.g. German/French, which run longer) never overflow. One size for all tiles.
  fitGhostText() {
    const cells = this.fanRow
      ? Array.from(this.fanRow.querySelectorAll('.lpv2-fan__ghost'))
      : [];
    if (!cells.length) return;
    const MAX = 9;
    const MIN = 5;
    const fits = (c) =>
      c.scrollWidth <= c.clientWidth + 0.5 && c.scrollHeight <= c.clientHeight + 0.5;
    let size = MAX;
    for (; size > MIN; size -= 0.5) {
      cells.forEach((c) => {
        c.style.fontSize = size + 'px';
      });
      if (cells.every(fits)) break;
    }
    cells.forEach((c) => {
      c.style.fontSize = size + 'px';
    });
  }

  // Clip line at the row's right edge, shown once the coins overflow (the row scrolls) —
  // bisects the half-visible coin at the scroll boundary.
  updateFanClip() {
    const row = this.fanRow;
    if (!row) return;
    row.classList.toggle('lpv2-fan__row--clip', row.scrollWidth - row.clientWidth > 1);
  }

  // Visually-hidden progress sentence for screen readers (live region after arm()).
  updateStatus(count) {
    if (!this.statusEl) return;
    const ms = this.milestones;
    const ds = this.trackEl ? this.trackEl.dataset : {};
    const maxQ = ms[ms.length - 1].quantity;
    let msg;
    if (count <= 0) {
      msg = ds.empty || '';
    } else {
      const current = ms.filter((m) => count >= m.quantity).pop();
      const message =
        current && this.overrides[current.quantity] && this.overrides[current.quantity].message;
      if (message != null && String(message).trim() !== '') {
        msg = message;
      } else if (count >= maxQ) {
        msg = ds.complete || '';
      } else {
        const next = ms.find((m) => m.quantity > count);
        msg = (ds.addMore || '').replace('{count}', next.quantity - count);
      }
    }
    // innerHTML (not textContent): the milestone `message` is richtext, so it may carry inline
    // formatting (e.g. <strong>). For this sr-only aria-live announcement, flatten paragraph /
    // <br> boundaries to spaces and drop the <p> wrappers — otherwise the paragraphs concatenate
    // with no separator ("more"+"for" → "morefor") and some screen readers read them run-together.
    // Single-paragraph messages (and the plain fallback strings) pass through unchanged.
    this.statusEl.innerHTML = String(msg == null ? '' : msg)
      .replace(/<\/p>\s*<p[^>]*>/gi, ' ')
      .replace(/<\/?p[^>]*>/gi, '')
      .replace(/<br\s*\/?>/gi, ' ');
  }
}

// Step celebration particles fired from a tracker node.
/* ════════════════════════ 3. Custom elements ════════════════════════ */

// Base: virtual-cart line bookkeeping shared by bundle + upsell items.
class LpVariantItem extends HTMLElement {
  addItem() {
    const item = this.data;
    const lines = this.cart?.data?.lines;
    if (lines) {
      const existing = lines.find((l) => l.variantId === item.variantId);
      if (existing) existing.quantity += item.quantity;
      else lines.push(item);
    }
    this.dispatchEvent(
      new CustomEvent('lp-cart:change', {
        bubbles: true,
        detail: { item, action: 'add', cart: this.cart.data },
      }),
    );
  }

  removeItem() {
    const item = this.data;
    const lines = this.cart?.data?.lines;
    if (lines) {
      const existing = lines.find((l) => l.variantId === item.variantId);
      if (existing && existing.quantity > item.quantity) existing.quantity -= item.quantity;
      else this.cart.data.lines = lines.filter((l) => l.variantId !== item.variantId);
    }
    this.dispatchEvent(
      new CustomEvent('lp-cart:change', {
        bubbles: true,
        detail: { item, action: 'remove', cart: this.cart.data },
      }),
    );
  }

  readData(type) {
    return {
      title: this.getAttribute('product-title') || '',
      type,
      productId: this.hasAttribute('product-id') ? Number(this.getAttribute('product-id')) : null,
      variantId: this.hasAttribute('variant-id') ? Number(this.getAttribute('variant-id')) : null,
      price: this.hasAttribute('variant-price') ? Number(this.getAttribute('variant-price')) : null,
      compareAtPrice: this.hasAttribute('variant-compare-at-price')
        ? Number(this.getAttribute('variant-compare-at-price'))
        : null,
      // For the footer tracker swatches (default/shade items carry these; upsells don't).
      handle: this.getAttribute('product-handle') || '',
      imageUrl: this.getAttribute('data-swatch-url') || '',
      quantity: 1,
    };
  }

  bindInput(selector) {
    this.input = this.querySelector(selector);
    if (!this.input) return;
    if (this.input.checked) this.addItem();
    this.input.addEventListener('input', (e) => {
      // Record a GENUINE user tap (the finder selects via synthetic, isTrusted:false
      // events) so the cart can show the milestone card-notification on the right card.
      // Set before addItem (which dispatches lp-cart:change synchronously), cleared after.
      if (this.input.checked && e.isTrusted && this.cart) this.cart._tapCard = this;
      if (this.input.checked) this.addItem();
      else this.removeItem();
      if (this.cart) this.cart._tapCard = null;
    });
  }
}

// Wire the in-card video. The card is a <label>, so the play button is the ONLY
// video control: it toggles play/pause and swallows its own click (preventDefault +
// stopPropagation) so it never toggles selection. We deliberately put NO handler on
// the <video>/pane — a click there falls through to the <label> and selects the
// card, exactly like the other pane. The button stays visible (icon swaps via the
// host's `is-playing` class) so there's always a control.
function wireCardVideo(host, videoSelector, opts) {
  const video = host.querySelector(videoSelector);
  const play = host.querySelector('[data-card-play]');
  if (!video || !play) return;
  const keepMuted = !!(opts && opts.keepMuted); // upsell plays silently

  play.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (video.paused) {
      if (!keepMuted) video.muted = false;
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      video.pause();
    }
  });
  video.addEventListener('playing', () => {
    host.classList.add('is-playing');
    play.setAttribute('aria-label', 'Pause video');
  });
  video.addEventListener('pause', () => {
    host.classList.remove('is-playing');
    play.setAttribute('aria-label', 'Play video');
  });
  // Non-looping video (the upsell plays once): when it ends, restore the play control and
  // rewind so a second tap replays from the start. No-op for looping videos (never end).
  video.addEventListener('ended', () => {
    host.classList.remove('is-playing');
    play.setAttribute('aria-label', 'Play video');
    video.currentTime = 0;
  });
}

// A selectable shade card; also wires the tap-to-play video in the "eye" pane.
class LpBundleItem extends LpVariantItem {
  connectedCallback() {
    // The finder result layout re-parents cards (moves them between grids), which
    // fires connectedCallback again — only wire once, or the input gets duplicate
    // listeners and the item is re-added to the cart.
    if (this._wired) return;
    this._wired = true;
    this.cart = this.closest('lp-cart');
    this.data = this.readData('default');
    this.bindInput('input.lpv2-bundle-input');
    this.warmCoinImage();
  }

  // The footer coin uses a separate image (data-swatch-url — often a dedicated 1:1 crop at a
  // different width than the card packshot), so it isn't fetched until the coin first renders
  // on selection — a visible lag on the first tap. Prefetch it into cache, piggybacking on the
  // card's own lazy-loaded packshot: once that main image loads (i.e. the card neared the
  // viewport), warm the coin URL. Cards far off-screen stay deferred, matching the packshot's
  // lazy behaviour; by the time a shade is in view to be selected, its coin is already cached.
  warmCoinImage() {
    const url = this.getAttribute('data-swatch-url');
    if (!url) return;
    const warm = () => {
      const pre = new Image();
      pre.src = url;
    };
    const img = this.querySelector('.lpv2-card__image img');
    if (!img || img.complete) {
      warm(); // no packshot, or it's already loaded (cache hit) → warm now
      return;
    }
    img.addEventListener('load', warm, { once: true });
    img.addEventListener('error', warm, { once: true }); // don't strand the coin on a bad packshot
  }

  // Dedup by variant. The same shade can be placed on several cards (e.g. a grid
  // placement that the finder also recommends, or the same product in two sections), so
  // selection is membership, NOT a per-card add: the cart holds at most ONE line (qty 1)
  // per variant, and every card showing that variant mirrors the selected state. Without
  // this, the base addItem would bump the shared line's quantity (a duplicate unit at
  // checkout) and the duplicate card would look unselected while already in the cart.
  addItem() {
    const item = this.data;
    const lines = this.cart?.data?.lines;
    const already = lines && lines.some((l) => l.variantId === item.variantId);
    if (lines && !already) lines.push(item); // qty stays 1 — never incremented
    this.mirrorSiblings(true);
    if (already) return; // already in the cart → no totals change, no event
    this.dispatchEvent(
      new CustomEvent('lp-cart:change', {
        bubbles: true,
        detail: { item, action: 'add', cart: this.cart.data },
      }),
    );
  }

  removeItem() {
    const item = this.data;
    const lines = this.cart?.data?.lines;
    // Uncheck the other cards for this variant first, then drop the single line.
    this.mirrorSiblings(false);
    const present = lines && lines.some((l) => l.variantId === item.variantId);
    if (lines) this.cart.data.lines = lines.filter((l) => l.variantId !== item.variantId);
    if (!present) return; // nothing was selected → no totals change, no event
    this.dispatchEvent(
      new CustomEvent('lp-cart:change', {
        bubbles: true,
        detail: { item, action: 'remove', cart: this.cart.data },
      }),
    );
  }

  // Mirror this card's checked state onto every OTHER card showing the same variant.
  // Programmatic `.checked =` does NOT fire an 'input' event, so the siblings' own
  // listeners don't re-enter add/removeItem — no loop, no double counting. Disabled
  // (sold-out) cards are left alone.
  mirrorSiblings(checked) {
    if (!this.cart) return;
    const vid = this.data.variantId;
    if (vid == null) return;
    this.cart.querySelectorAll('lp-bundle-item').forEach((el) => {
      if (el === this) return;
      if (Number(el.getAttribute('variant-id')) !== vid) return;
      const inp = el.querySelector('input.lpv2-bundle-input');
      if (inp && !inp.disabled && inp.checked !== checked) inp.checked = checked;
    });
  }
}

// A gift / paid add-on row with three display states (see lp-product-tile):
//   earned  — shade count >= trigger: free, locked in (the Hy app zeroes it).
//   added   — user added it below the threshold: paid, removable.
//   default — addable.
// Cart membership = earned OR userAdded. `userAdded` is remembered, so a paid add
// flips to free when the threshold is reached, and back to paid if it drops below.
// Add-ons (no trigger) simply toggle default <-> added.
class LpUpsellItem extends LpVariantItem {
  connectedCallback() {
    if (this._wired) return; // guard re-init if ever re-parented (see LpBundleItem)
    this._wired = true;
    this.cart = this.closest('lp-cart');
    this.data = this.readData('upsell');
    this.input = this.querySelector('input.lpv2-upsell-input');
    if (!this.input) return;

    this.triggerQuantity = this.hasAttribute('trigger-quantity')
      ? Number(this.getAttribute('trigger-quantity'))
      : 0;
    this.isGift = this.triggerQuantity > 0;
    this.userAdded = this.input.checked; // preselected add-ons start added
    this.earned = false;

    if (this.input.checked) this.addItem();

    this.input.addEventListener('input', () => this.onToggle());
    this.initCardVideo();

    if (this.isGift) {
      this.cart.addEventListener('lp-cart:change', (event) => {
        if (event.detail.item.type !== 'default') return;
        this.syncEarned();
      });
      this.syncEarned();
    } else {
      this.setState();
    }

    // Paint this tile's price on load. The cart's change-driven paint may have already
    // run for the preselected shades BEFORE this upsell upgraded (tiles sit after the
    // shade grid in the DOM), so its discounted pill was skipped. Re-run now that this
    // element's data exists — idempotent, and covers either upgrade order.
    if (this.cart && typeof this.cart.updateUpsellPrices === 'function') {
      this.cart.updateUpsellPrices(this.cart.data);
    }
  }

  initCardVideo() {
    wireCardVideo(this, '.lpv2-up__video', { keepMuted: true });
  }

  shadeCount() {
    return this.cart.data.lines
      .filter((l) => l.type === 'default')
      .reduce((acc, l) => acc + l.quantity, 0);
  }

  // User tapped the row.
  onToggle() {
    if (this.earned) {
      // Locked in as free — keep it in the cart, ignore the toggle.
      this.input.checked = true;
      return;
    }
    this.userAdded = this.input.checked;
    if (this.input.checked) this.addItem();
    else this.removeItem();
    this.setState();
  }

  // Recompute earned from the live shade count; reconcile cart membership.
  syncEarned() {
    this.earned = this.shadeCount() >= this.triggerQuantity;
    const shouldBeIn = this.earned || this.userAdded;
    if (this.input.checked !== shouldBeIn) {
      this.input.checked = shouldBeIn;
      if (shouldBeIn) this.addItem();
      else this.removeItem();
    }
    this.setState();
  }

  setState() {
    const state = this.earned ? 'earned' : this.userAdded ? 'added' : 'default';
    this.setAttribute('data-state', state);
  }
}

// The orchestrator element: owns the virtual cart, checkout, and the footer paint;
// delegates the tracker / burst to the UI widgets above.
class LpCart extends HTMLElement {
  constructor() {
    super();
    this.data = {
      lines: [],
      localization: {
        country: { isoCode: this.getAttribute('country') || null },
      },
      currency: {
        format: this.getAttribute('money-format') || '€{{amount_with_comma_separator}}',
        currencyCode: this.getAttribute('currency') || null,
      },
    };
  }

  connectedCallback() {
    this.basePrice = this.getAttribute('base_price') || 'price';
    // Dev aid: add a `debug` attribute to <lp-cart> to log the discount config.
    this.debug = this.hasAttribute('debug');

    Shopify.cartQueue.enqueue({
      url: `${Shopify.routes.root}cart/clear.js`,
      options: { method: 'POST' },
      callback: () => {},
      error: () => console.error('Cart clear failed!'),
    });

    this.tracker = new LpTracker(this);
    // Milestone card-notifications already shown this page load (in-memory; resets on
    // refresh) — each tier's message types onto a card at most once per session.
    this._notePlayed = new Set();

    this.addEventListener('lp-cart:change', (event) => {
      if (!event?.detail?.cart) return;
      const cart = event.detail.cart;
      const { original, discounted } = getTotals(cart, this.basePrice);
      const cartCount = cart.lines.reduce((acc, l) => acc + l.quantity, 0);
      // Progress measures SELECTED products only — auto-added free gifts (type
      // 'upsell') must not count toward the milestone thresholds.
      const count = selectionCount(cart.lines);

      this.updateSection(original, discounted, cartCount, cart.currency.format);
      this.updateCardPrices(cart);
      this.updateUpsellPrices(cart);
      const adv = this.tracker.update(count, original, discounted, cartCount);
      this.tracker.renderFan(cart.lines);
      // Milestone card-notification: type the reached tier's message onto the just-tapped
      // card. Genuine user taps only (this._tapCard is unset for the finder's synthetic
      // selections), and once per tier per page load (this._notePlayed).
      if (this.ready && adv.advanced && this._tapCard && this._tapCard.tagName === 'LP-BUNDLE-ITEM') {
        const ms = this.tracker.milestones[adv.idx];
        if (ms) this.showCardNote(this._tapCard, ms.quantity);
      }
      // The buy-now guard only makes sense at a single shade — dismiss it the moment a
      // second is added or the basket empties.
      if (count !== 1) this.closeGuard();
      this.selectionCount = count;
    });

    // Restore from bfcache cleanly (cart was cleared on load).
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) window.location.reload();
    });

    this.querySelectorAll('[checkout-button]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.hasAttribute('disabled')) return;
        if (this.maybeGuard()) return; // single-shade nudge intercepted this tap
        if (this.maybeUpsell()) return; // interstitial upsell takeover intercepted this tap
        this.submit();
      });
    });

    // Buy-now re-assurance guard (once per visit, in-memory).
    this.guardEl = this.querySelector('[data-guard]');
    this.guardSeen = false;
    const guardBtn = this.guardEl && this.guardEl.querySelector('[data-guard-btn]');
    if (guardBtn) guardBtn.addEventListener('click', () => this.guardAction());
    if (this.guardEl) {
      this.guardEl.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeGuard();
      });
    }

    // Optional interstitial upsell takeover — present only when a checkout-upsell block is
    // placed + enabled (see snippets/lp-checkout-upsell.liquid). No-op on every other template.
    this.upsellEl = this.querySelector('[data-upsell]');
    this._upsellShown = false; // once-per-page-load guard (in-memory)
    if (this.upsellEl) this.initUpsell();

    const currency = (this.data.currency && this.data.currency.currencyCode) || 'EUR';
    const milestones = buildMilestones(this, currency, this.debug);
    if (this.debug) console.log('[lp-cart] savings tracker milestones →', milestones);
    this.tracker.init(milestones, this.data.currency.format);
    this.observeFooterHeight();
    this.observeCardVisibility();

    // Arm celebrations only after the initial pre-selected cascade settles (items
    // dispatch lp-cart:change synchronously on upgrade; this timeout runs after).
    setTimeout(() => {
      this.ready = true;
      this.tracker.arm();
      this.captureCardHomes(); // snapshot the default layout/selection for reset
    }, 0);

    // The finder (a separate section) signals completion → reorganise into the result
    // layout (curated/secondary/explore); Start-over → restore the default. The reuse
    // seam: same cards, re-parented.
    document.addEventListener('lpv2-finder:complete', (event) =>
      this.applyFinderResult(event.detail),
    );
    document.addEventListener('lpv2-finder:scroll', () => this.scrollToFinderResult());
    document.addEventListener('lpv2-finder:reset', () => this.restoreDefaultLayout());
  }

  // Card-notification: type the reached tier's milestone message onto `card`, over a
  // dark overlay (design's stepBurst). Shown at most ONCE per tier per page load
  // (this._notePlayed, in-memory → resets on refresh). Reduced-motion shows it instantly.
  showCardNote(card, tier) {
    const ov = (this.tracker && this.tracker.overrides) || {};
    const msg = ov[tier] && ov[tier].message;
    if (!card || !msg || this._notePlayed.has(tier)) return;
    this._notePlayed.add(tier);

    if (this._noteTimers) this._noteTimers.forEach(clearTimeout);
    this._noteTimers = [];
    // Only one note lives at a time — the timers above are cleared globally, so remove ANY
    // existing note across the whole stack, not just this card. If the previous note was on a
    // *different* card (rapid selection of a second shade), scoping the removal to `card` would
    // leave that note orphaned with its removal timer already cancelled — it sticks forever and
    // tanks conversion. The new note always supersedes the previous one, on any card.
    this.querySelectorAll('.lpv2-card__note').forEach((n) => n.remove());

    const host = document.createElement('div');
    host.className = 'lpv2-card__note';
    // Per-tier presentation from the Milestone block: text size + the two highlight colours
    // (bold → colour 1, italic → colour 2). Set as CSS vars on the note host so they cascade to
    // the typed character spans (and the reduced-motion innerHTML path).
    const tierOv = ov[tier] || {};
    if (tierOv.noteSize) host.style.setProperty('--note-size', tierOv.noteSize + 'px');
    if (tierOv.colorBold) host.style.setProperty('--note-color-bold', tierOv.colorBold);
    if (tierOv.colorEm) host.style.setProperty('--note-color-em', tierOv.colorEm);
    const textEl = document.createElement('div');
    textEl.className = 'lpv2-card__note-text';
    host.appendChild(textEl);
    card.appendChild(host);

    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      // Same normalization as the typed path: richtext paragraphs → <br> (no-op for inline_richtext).
      textEl.innerHTML = this._noteLines(msg).replace(/\n/g, '<br>');
      host.classList.add('is-shown', 'is-typing');
      this._noteTimers.push(setTimeout(() => host.remove(), 2600));
      return;
    }

    const FADE = 260;
    void host.offsetWidth; // flush so the opacity transition runs
    host.classList.add('is-shown'); // overlay fades in first
    this._noteTimers.push(
      setTimeout(() => {
        host.classList.add('is-typing');
        // Build every char up front (final weight/colour baked in, all hidden) and reveal
        // by colour only, so nothing shifts while typing (design's no-shift trick).
        const { frag, spans } = this._buildNote(msg);
        textEl.appendChild(frag);
        const cur = document.createElement('span');
        cur.className = 'lpv2-card__note-cur';
        textEl.insertBefore(cur, spans[0] || null);
        let i = 0;
        const tick = () => {
          if (i > 0 && spans[i - 1]) spans[i - 1].classList.remove('is-hidden');
          if (spans[i]) textEl.insertBefore(cur, spans[i]);
          else textEl.appendChild(cur);
          i += 1;
          if (i <= spans.length) {
            this._noteTimers.push(setTimeout(tick, 33));
          } else {
            this._noteTimers.push(
              setTimeout(() => {
                if (cur.parentNode) cur.remove();
                host.classList.remove('is-typing', 'is-shown'); // fade out
                this._noteTimers.push(setTimeout(() => host.remove(), FADE + 60));
              }, 1620),
            );
          }
        };
        tick();
      }, FADE),
    );
  }

  // Split a message (may contain <strong>…</strong>) into one hidden <span> per character,
  // bold runs tagged, for the char-by-char reveal above.
  // Normalize a message's paragraph/break markup to plain \n line breaks, keeping <strong>/<em>.
  // `inline_richtext` never contains <p>/<br>, so this is a NO-OP for today's messages — it just
  // readies the note for a future switch of `message` to richtext (which emits <p>…</p> paragraphs).
  _noteLines(msg) {
    return String(msg == null ? '' : msg)
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
      .replace(/<\/?p[^>]*>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n');
  }

  _buildNote(msg) {
    const frag = document.createDocumentFragment();
    const spans = [];
    const normalized = this._noteLines(msg); // computed once — the regex below is stateful (global)
    const re = /<strong>(.*?)<\/strong>|<em>(.*?)<\/em>|([^<]+)/g;
    let m;
    while ((m = re.exec(normalized))) {
      const bold = m[1] != null;
      const em = m[2] != null;
      const txt = bold ? m[1] : em ? m[2] : m[3];
      const mod = bold ? ' is-bold' : em ? ' is-em' : '';
      for (let k = 0; k < txt.length; k += 1) {
        // A newline (a richtext paragraph break) lays out a <br> rather than a revealed char, so the
        // multi-line shape is set from the start and characters still reveal without reflow.
        if (txt[k] === '\n') {
          frag.appendChild(document.createElement('br'));
          continue;
        }
        const s = document.createElement('span');
        s.className = 'lpv2-card__note-cc is-hidden' + mod;
        s.textContent = txt[k];
        frag.appendChild(s);
        spans.push(s);
      }
    }
    return { frag, spans };
  }

  // Snapshot each shade card's home (parent, position, selected, description) so the
  // result layout can be undone exactly on Start over.
  captureCardHomes() {
    if (this.cardHomes) return; // capture the DEFAULT state once
    this.cardHomes = Array.from(this.querySelectorAll('lp-bundle-item')).map((el) => {
      const input = el.querySelector('input');
      const desc = el.querySelector('.lpv2-card__desc');
      return {
        el: el,
        parent: el.parentNode,
        checked: input ? input.checked : false,
        desc: desc ? desc.innerHTML : null,
      };
    });
  }

  // Swap a matched card's description for its bespoke shade copy. Plain-text (textContent)
  // since the outcomes JSON carries copy, not markup; the original richtext is restored
  // from the cardHomes snapshot on Start-over.
  setCardText(el, text) {
    if (!text) return;
    const d = el.querySelector('.lpv2-card__desc');
    if (d) d.textContent = text;
  }

  // Paint the checkout footer totals + enable/disable the button.
  updateSection(originalTotal, discountedTotal, cartCount, format) {
    const savedAmount = Math.max(originalTotal - discountedTotal, 0);
    const savedPercent = discountedTotal > 0 ? Math.round((savedAmount * 100) / originalTotal) : 0;
    const originalFmt = formatMoney(originalTotal, format);
    const discountedFmt = formatMoney(discountedTotal, format);
    const savedFmt = formatMoney(savedAmount, format);

    if (cartCount < 1) {
      this.setAttribute('disable-checkout', '');
      this.querySelectorAll('[checkout-button]').forEach((b) => b.setAttribute('disabled', ''));
      this.querySelectorAll('[data-only-discount]').forEach((c) => c.classList.add('hidden'));
      return;
    }

    this.removeAttribute('disable-checkout');
    this.querySelectorAll('[checkout-button]').forEach((b) => b.removeAttribute('disabled'));
    const discounted = originalTotal > discountedTotal;
    this.querySelectorAll('[data-original-total]').forEach((c) => {
      c.innerHTML = originalFmt;
      c.classList.toggle('hidden', !discounted);
    });
    this.querySelectorAll('[data-discounted-total]').forEach((c) => (c.innerHTML = discountedFmt));
    this.querySelectorAll('[data-only-discount]').forEach((c) =>
      c.classList.toggle('hidden', !discounted),
    );
    this.querySelectorAll('[data-item-count]').forEach((c) => (c.innerHTML = cartCount));
    this.querySelectorAll('[data-saved-amount]').forEach((c) => (c.innerHTML = savedFmt));
    this.querySelectorAll('[data-saved-percent]').forEach(
      (c) => (c.innerHTML = savedPercent + '%'),
    );
  }

  // Buy-now re-assurance guard. Returns true when it intercepts the checkout tap.
  // First tap with a single shade (and a higher tier still to reach) → show the nudge;
  // a second tap while it's up = "continue anyway"; once seen, checkout proceeds.
  maybeGuard() {
    const g = this.guardEl;
    if (!g) return false;
    if (!g.hidden) {
      this.closeGuard();
      return false;
    }
    const ms = this.tracker.milestones;
    if (this.selectionCount === 1 && !this.guardSeen && ms && ms.length > 1) {
      this.openGuard();
      return true;
    }
    return false;
  }

  openGuard() {
    const g = this.guardEl;
    if (!g) return;
    clearTimeout(this._guardTimer);
    const done = !!(this.finderRecommended && this.finderRecommended.length);
    g.classList.toggle('is-done', done);
    this._guardReturnFocus = document.activeElement;
    g.hidden = false;
    void g.offsetWidth; // reflow so the slide-up transition runs
    g.classList.add('show');
    this.guardSeen = true;
    const btn = g.querySelector('[data-guard-btn]');
    if (btn) btn.focus();
  }

  closeGuard() {
    const g = this.guardEl;
    if (!g || g.hidden) return;
    const hadFocus = g.contains(document.activeElement);
    g.classList.remove('show');
    clearTimeout(this._guardTimer);
    this._guardTimer = setTimeout(() => {
      g.hidden = true;
    }, 400);
    // Only restore focus if it was trapped inside the guard (Esc / CTA close); don't
    // steal it when the guard auto-dismisses on a selection change elsewhere.
    if (hadFocus && this._guardReturnFocus && typeof this._guardReturnFocus.focus === 'function') {
      this._guardReturnFocus.focus();
    }
    this._guardReturnFocus = null;
  }

  // Guard CTA: if the finder has run, glide back to the matched shades; otherwise reveal
  // and start the Shade Assistant.
  guardAction() {
    const done = !!(this.finderRecommended && this.finderRecommended.length);
    this.closeGuard();
    if (done) {
      this.scrollToFinderResult();
      return;
    }
    const finder = document.querySelector('lp-product-finder');
    if (!finder) return;
    finder.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const start = finder.querySelector('[data-finder-start]');
    if (start) start.click();
  }

  // Per-card price under the live bundle discount. The Hy app only gives a whole-
  // cart total, so the discount is allocated to each SELECTED card by ratio
  // (discounted/original) — for a flat % bundle that's the same % on every card.
  //   now = price × ratio     was = compare-at (if any) else single price (struck)
  // Unselected cards (or no active discount) show the full price, no strike. The
  // footer total stays authoritative; per-card values are rounded for display.
  //
  // IMPORTANT: the ratio is computed over the SHADE lines only (type 'default').
  // The whole-cart total folds in auto-added free gifts (100% off → €0) and paid
  // add-ons; using that ratio would smear their savings onto the shade cards,
  // overstating each shade's discount. A shades-only cart isolates the bundle
  // discount that actually applies to the shades (and the scale tier still keys
  // off shade quantity, which is unchanged by dropping the other lines).
  updateCardPrices(cart) {
    const shadeCart = {
      ...cart,
      lines: cart.lines.filter((l) => l.type === 'default'),
    };
    const { original: shadeOriginal, discounted: shadeDiscounted } = getTotals(
      shadeCart,
      this.basePrice,
    );
    const ratio = shadeOriginal > 0 ? shadeDiscounted / shadeOriginal : 1;
    const hasDiscount = ratio < 0.999;
    const format = (cart.currency && cart.currency.format) || this.data.currency.format;
    const selectedIds = new Set(
      cart.lines.filter((l) => l.type === 'default').map((l) => l.variantId),
    );

    this.querySelectorAll('lp-bundle-item').forEach((item) => {
      const nowEl = item.querySelector('.lpv2-card__now');
      const wasEl = item.querySelector('.lpv2-card__was');
      if (!nowEl) return;
      const price = Number(item.getAttribute('variant-price')) || 0;
      const compareAt = item.hasAttribute('variant-compare-at-price')
        ? Number(item.getAttribute('variant-compare-at-price'))
        : 0;
      const selected = selectedIds.has(Number(item.getAttribute('variant-id')));

      if (selected && hasDiscount) {
        // Discounted: now = proportional bundle price; was = compare-at or price.
        nowEl.innerHTML = formatMoney(price * ratio * 100, format);
        if (wasEl) {
          const wasVal = compareAt > price ? compareAt : price;
          wasEl.innerHTML = formatMoney(wasVal * 100, format);
          wasEl.hidden = false;
        }
      } else {
        // Full price; keep the static compare-at strike if the product has one.
        nowEl.innerHTML = formatMoney(price * 100, format);
        if (wasEl) {
          if (compareAt > price) {
            wasEl.innerHTML = formatMoney(compareAt * 100, format);
            wasEl.hidden = false;
          } else {
            wasEl.hidden = true;
          }
        }
      }
    });
  }

  // Effective per-unit price (major currency units) of an upsell under the live cart.
  // The Hy app discounts cart lines but exposes only whole-cart totals, so derive the
  // line's price by diffing the discounted total WITH vs WITHOUT this line — no rule is
  // hardcoded here, so "2nd add-on 50% off at 3 shades" (or any config) is honoured as
  // data. When the upsell isn't in the cart we simulate adding one unit, so the tile can
  // advertise the discount before it's selected. Falls back to full price (no Hy app, or
  // no real saving). Skips earned gifts — their free/struck pill is handled by CSS.
  discountedUnitPrice(el) {
    const full = Number(el.getAttribute('variant-price')) || 0;
    const api = lpDiscountApi();
    if (!api || !el.data) return full;
    return lineDiscountedPrice({
      cart: this.data,
      line: el.data,
      full,
      calcDiscounted: (c) => api.calculateDiscountedTotal(c),
    });
  }

  // Reflect any live per-line discount on the upsell tiles' price pill: struck full
  // price (.lpv2-up__was) + discounted "now" (.lpv2-up__now), toggled via .lpv2-up--discounted.
  // The discounted unit price is stamped on the element (_lpUnitPrice) so the footer's
  // companion pill (LpTracker.updateCompanions) reads the same figure. Earned (free-gift)
  // tiles keep their own free/struck treatment, so we don't touch their pill.
  updateUpsellPrices(cart) {
    const format = (cart.currency && cart.currency.format) || this.data.currency.format;
    // Earned status from the LIVE selection count, NOT the tile's data-state: that
    // attribute is set by each upsell's own lp-cart:change listener, which is registered
    // after LpCart's (the parent upgrades first) and so runs AFTER this. Reading it here
    // is a step stale — e.g. dropping 4→3 left the pill on the earned/full price instead
    // of reverting to the discount. cart.lines is already current when this fires.
    const count = selectionCount(cart.lines);
    this.querySelectorAll('lp-upsell-item').forEach((el) => {
      const full = Number(el.getAttribute('variant-price')) || 0;
      const trigger = Number(el.getAttribute('trigger-quantity')) || 0;
      const earned = isEarned(count, trigger);
      const unit = earned ? full : this.discountedUnitPrice(el);
      el._lpUnitPrice = unit;
      const hasDiscount = !earned && unit < full - 0.0001;
      el.classList.toggle('lpv2-up--discounted', hasDiscount);
      if (earned) return; // earned pill (struck full + "Free") is CSS-driven
      const nowEl = el.querySelector('.lpv2-up__now');
      const wasEl = el.querySelector('.lpv2-up__was');
      if (nowEl) nowEl.innerHTML = formatMoney((hasDiscount ? unit : full) * 100, format);
      if (wasEl && hasDiscount) wasEl.innerHTML = formatMoney(full * 100, format);
    });
  }

  multiATC() {
    const payload = {
      items: this.data.lines.map((line) => {
        const properties = { _lp_exclusive: 'true' };
        // Flag the interstitial upsell line so the order (→ Flow) can attribute it.
        if (line.checkoutUpsell) properties._lp_checkout_upsell = 'true';
        return { id: line.variantId, quantity: line.quantity, properties };
      }),
    };
    Shopify.cartQueue.enqueue({
      url: `${Shopify.routes.root}cart/add.js`,
      options: {
        body: JSON.stringify(payload),
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
      callback: () => {
        // Write the finder attributes (if any), then go to checkout.
        this.persistFinderAttributes(() => {
          setTimeout(() => {
            this.setLoading(false);
            window.location.href = `${Shopify.routes.root}checkout`;
          }, payload.items.length * 100);
        });
      },
      error: () => {
        this.setLoading(false);
        // Re-enable the interstitial dialog (if it's driving this submit) so the shopper isn't
        // stranded on a spinning, locked dialog when the add fails.
        this._upsellSubmitting = false;
        this.setUpsellLoading(false);
        console.error('Cart add failed!');
      },
    });
  }

  // Persist the finder run as hidden cart attributes (→ order) before checkout,
  // namespaced by finder id (e.g. "lp_shade"):
  //   _<id>_<trait>          — one flat attribute per answer (Flow-friendly)
  //   _<id>_recommended      — the recommended variant ids as CSV (Flow-facing primary)
  //   _<id>_recommended_json — additive JSON [{variantId, title}] (merchant-readable)
  // Never blocks checkout if the update fails. `next` continues the flow.
  persistFinderAttributes(next) {
    const id = this.finderId || 'lp_shade';
    const attributes = {};
    // Flag that this order came through a landing page — we only need the fact, not which
    // page. Cart attribute values are strings, so store "true" (a raw boolean is coerced to
    // "true" anyway, but the string is the documented type + what Flow compares against).
    if (window.location.pathname.split('/').filter(Boolean).length) {
      attributes['_lp_landing_page'] = 'true';
    }
    // Flat, Flow-friendly hidden attributes (no JSON, since Shopify Flow handles it poorly):
    // one per trait (keys match the canonical trait.* metafields), the quiz id, and the
    // recommended variant ids as a CSV. Each maps 1:1 to an order/customer metafield in Flow.
    const traits = this.finderTraits || {};
    const hasTraits = Object.keys(traits).length > 0;
    const hasRecommended = this.finderRecommended && this.finderRecommended.length;
    if (hasTraits || hasRecommended) {
      attributes['_' + id + '_quiz'] = id;
    }
    if (hasTraits) {
      Object.keys(traits).forEach((axis) => {
        attributes['_' + id + '_' + axis] = traits[axis];
      });
    }
    if (hasRecommended) {
      attributes['_' + id + '_recommended'] = this.finderRecommended.join(',');
      // Additive JSON twin: variant id + product name, for merchant readability on the
      // order. The flat CSV above stays the Flow-facing primary (Flow handles JSON poorly).
      const full = this.finderRecommendedFull;
      if (full && full.length) {
        attributes['_' + id + '_recommended_json'] = JSON.stringify(full);
      }
    }
    if (!Object.keys(attributes).length) {
      next();
      return;
    }
    Shopify.cartQueue.enqueue({
      url: `${Shopify.routes.root}cart/update.js`,
      options: {
        body: JSON.stringify({ attributes }),
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
      callback: () => next(),
      error: () => {
        console.error('Finder cart attributes update failed');
        next();
      },
    });
  }

  // Submitting state: dim + spinner via the `loading` class, and aria-busy on the
  // checkout button(s) for screen readers.
  setLoading(on) {
    this.classList.toggle('loading', on);
    this.querySelectorAll('[checkout-button]').forEach((b) => {
      if (on) b.setAttribute('aria-busy', 'true');
      else b.removeAttribute('aria-busy');
    });
  }

  submit() {
    this.setLoading(true);
    Shopify.cartQueue.enqueue({
      url: `${Shopify.routes.root}cart/clear.js`,
      options: { method: 'POST' },
      callback: () => {},
      error: () => console.error('Cart clear failed!'),
    });
    this.multiATC();
  }

  /* ─────────────────────── Interstitial upsell takeover ───────────────────────
     A full-screen <dialog> shown on the FINAL Buy Now tap (once per page load), offering
     one extra product before checkout. Add/No KEEP the dialog open and drive checkout in place
     (acting button spins). The ✕ / Esc instead CLOSE the dialog and let the page's own Buy Now
     spinner run — checkout proceeds without the upsell either way.
     All animation (entrance, video, review carousel) runs ONLY while the dialog is open. */

  initUpsell() {
    const el = this.upsellEl;
    const add = el.querySelector('[data-upsell-add]');
    const no = el.querySelector('[data-upsell-no]');
    const close = el.querySelector('[data-upsell-close]');
    if (add) add.addEventListener('click', () => this.upsellAdd());
    if (no) no.addEventListener('click', () => this.upsellDecline());
    // The ✕ (and Esc) close the dialog and reveal the page's own spinner — never spins itself.
    if (close) close.addEventListener('click', () => this.upsellClose());
    el.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.upsellClose();
    });
  }

  // Returns true when it intercepts the checkout tap (opens the takeover). Only the first
  // final-buy-now of the visit opens it; later taps fall through to submit().
  maybeUpsell() {
    if (!this.upsellEl || this._upsellShown) return false;
    this.openUpsell();
    return true;
  }

  // Build the upsell cart line from the dialog's data-* attributes. type:'upsell' so it never
  // counts toward the shade milestone ladder; `checkoutUpsell` flags it for the ATC property.
  buildUpsellLine() {
    const el = this.upsellEl;
    if (!el) return null;
    const price = Number(el.getAttribute('data-variant-price')) || 0;
    return {
      variantId: Number(el.getAttribute('data-variant-id')) || null,
      productId: Number(el.getAttribute('data-product-id')) || null,
      title: el.getAttribute('data-product-title') || '',
      price,
      compareAtPrice: el.hasAttribute('data-variant-compare-at-price')
        ? Number(el.getAttribute('data-variant-compare-at-price'))
        : null,
      quantity: 1,
      type: 'upsell',
      checkoutUpsell: true,
    };
  }

  // Paint the dialog's price row from the LIVE Hy discount: now = the upsell's marginal
  // discounted unit price under the current cart (lineDiscountedPrice diffs the total with vs
  // without it); was = compare-at (if any) else full; save flag = override, else computed %.
  // No calculator (dev theme) → full price, no strike/save.
  // Format money for the dialog, dropping the ".00" cents on whole amounts (e.g. £18.00 → £18)
  // by swapping the money placeholder to its no-decimals variant. Fractional amounts keep their
  // decimals. Locale-safe (works for comma-separator formats; suffix-currency positions too).
  upsellMoney(cents, format) {
    if (Math.round(cents) % 100 !== 0) return formatMoney(cents, format);
    const noDec = format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      const swap =
        key === 'amount'
          ? 'amount_no_decimals'
          : key === 'amount_with_comma_separator'
            ? 'amount_no_decimals_with_comma_separator'
            : key; // already no-decimals (or apostrophe variant) → unchanged
      return '{{' + swap + '}}';
    });
    return formatMoney(cents, noDec);
  }

  paintUpsellPrice() {
    const el = this.upsellEl;
    if (!el) return;
    const line = this.buildUpsellLine();
    const full = line.price || 0;
    const format = this.data.currency.format;
    const api = lpDiscountApi();
    let now = full;
    if (api && line.variantId) {
      now = lineDiscountedPrice({
        cart: this.data,
        line,
        full,
        calcDiscounted: (c) => api.calculateDiscountedTotal(c),
      });
    }
    const compareAt = line.compareAtPrice && line.compareAtPrice > full ? line.compareAtPrice : 0;
    const hasDiscount = now < full - 0.0001;
    const effective = hasDiscount ? now : full;
    const wasVal = compareAt > 0 ? compareAt : full;
    const showWas = hasDiscount || compareAt > 0;

    const nowEl = el.querySelector('[data-upsell-now]');
    const wasEl = el.querySelector('[data-upsell-was]');
    const saveEl = el.querySelector('[data-upsell-save]');
    if (nowEl) nowEl.innerHTML = this.upsellMoney(effective * 100, format);
    if (wasEl) {
      if (showWas) {
        wasEl.innerHTML = this.upsellMoney(wasVal * 100, format);
        wasEl.hidden = false;
      } else {
        wasEl.hidden = true;
      }
    }
    if (saveEl) {
      const override = el.getAttribute('data-save-label') || '';
      let label = '';
      if (override) {
        label = override;
      } else if (showWas && wasVal > 0) {
        const pct = Math.round(((wasVal - effective) * 100) / wasVal);
        if (pct > 0) label = (el.getAttribute('data-save-tpl') || 'Save {pct}%').replace('{pct}', pct);
      }
      if (label) {
        saveEl.textContent = label;
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
    }
  }

  openUpsell() {
    const el = this.upsellEl;
    this._upsellShown = true;
    this.paintUpsellPrice();
    if (typeof el.showModal === 'function') {
      try {
        el.showModal();
      } catch (e) {
        el.setAttribute('open', ''); // already-open or unsupported state → non-modal fallback
      }
    } else {
      el.setAttribute('open', ''); // no <dialog> support (old WebView) → plain overlay
    }
    document.body.style.overflow = 'hidden';
    void el.offsetWidth; // flush so the fade/entrance transition runs
    requestAnimationFrame(() => el.classList.add('is-open'));
    this.startUpsellVideo();
    this.startUpsellReviews();
    const add = el.querySelector('[data-upsell-add]');
    if (add) {
      try {
        add.focus();
      } catch (e) {}
    }
  }

  // Close + stop ALL dialog animation (carousel timer + video). Called on every exit path;
  // each path then submits, so the page redirects immediately after.
  closeUpsell() {
    const el = this.upsellEl;
    if (!el) return;
    el.classList.remove('is-open');
    this.stopUpsellReviews();
    this.pauseUpsellVideo();
    document.body.style.overflow = '';
    if (typeof el.close === 'function' && el.open) {
      try {
        el.close();
      } catch (e) {
        el.removeAttribute('open');
      }
    } else {
      el.removeAttribute('open');
    }
  }

  upsellAdd() {
    if (this._upsellSubmitting) return; // guard against double taps
    const line = this.buildUpsellLine();
    if (line && line.variantId) {
      const lines = this.data.lines;
      if (!lines.some((l) => l.variantId === line.variantId)) lines.push(line);
    }
    this.driveUpsellCheckout(this.upsellEl.querySelector('[data-upsell-add]'));
  }

  upsellDecline() {
    if (this._upsellSubmitting) return;
    this.driveUpsellCheckout(this.upsellEl.querySelector('[data-upsell-no]'));
  }

  // ✕ / Esc: CLOSE the dialog (revealing the page) and drive checkout without the upsell — so
  // the page's own Buy Now spinner runs. Distinct from Add/No, which keep the dialog open.
  upsellClose() {
    if (this._upsellSubmitting) return;
    this._upsellSubmitting = true;
    this.closeUpsell();
    this.submit();
  }

  // Exit paths KEEP THE DIALOG OPEN and drive checkout — the page underneath never reappears
  // (the dialog is a one-way gate, like the design's "Buy Now is the only way forward"). Lock
  // the controls + spin the acting button, then submit; the redirect follows. Only a FAILED
  // submit resets the state (see multiATC's error handler) so the shopper can retry.
  driveUpsellCheckout(actingBtn) {
    this._upsellSubmitting = true;
    this.setUpsellLoading(true, actingBtn);
    this.submit();
  }

  // Toggle the dialog's submitting state: disable Add / No / ✕ (no going back) and spin the
  // acting button. No-op when no dialog is present, so it's safe on the direct-checkout path.
  setUpsellLoading(on, actingBtn) {
    const el = this.upsellEl;
    if (!el) return;
    el.classList.toggle('is-loading', on);
    el.setAttribute('aria-busy', on ? 'true' : 'false');
    el.querySelectorAll('[data-upsell-add], [data-upsell-no], [data-upsell-close]').forEach((b) => {
      if (on) b.setAttribute('disabled', '');
      else b.removeAttribute('disabled');
      b.classList.remove('is-busy');
    });
    if (on && actingBtn) actingBtn.classList.add('is-busy');
  }

  // Autoplay the muted product video — only while the dialog is open. Reduced motion: leave it
  // paused so the poster stands in (no autoplay).
  startUpsellVideo() {
    const v = this.upsellEl && this.upsellEl.querySelector('[data-upsell-video]');
    if (!v) return;
    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    try {
      v.muted = true;
      v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {}
  }

  pauseUpsellVideo() {
    const v = this.upsellEl && this.upsellEl.querySelector('[data-upsell-video]');
    if (v) {
      try {
        v.pause();
      } catch (e) {}
    }
  }

  // Rolling single-review carousel — advances one review every 2s, snapping back at the
  // duplicated tail. The interval runs ONLY between start (open) and stop (close), never in the
  // background. Reduced motion / a single review: static, no timer.
  startUpsellReviews() {
    this.stopUpsellReviews();
    const track = this.upsellEl && this.upsellEl.querySelector('[data-upsell-rv-track]');
    if (!track) return;
    if (!track._loopPrepared) {
      const items = track.querySelectorAll('.lpv2-upsell__rv-item');
      this._upsellReviewCount = items.length;
      if (items.length > 1) track.appendChild(items[0].cloneNode(true)); // seamless-loop tail
      track._loopPrepared = true;
    }
    const n = this._upsellReviewCount || 0;
    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || n <= 1) return;
    const H = 58; // one review row height (mirrors the CSS)
    let idx = 0;
    this._upsellRvTimer = setInterval(() => {
      idx += 1;
      track.style.transition = 'transform .5s cubic-bezier(.4,0,.2,1)';
      track.style.transform = 'translateY(-' + idx * H + 'px)';
      if (idx === n) {
        setTimeout(() => {
          track.style.transition = 'none';
          track.style.transform = 'translateY(0)';
          idx = 0;
          void track.offsetWidth; // flush the reset before the next animated step
        }, 520);
      }
    }, 2000);
  }

  stopUpsellReviews() {
    if (this._upsellRvTimer) {
      clearInterval(this._upsellRvTimer);
      this._upsellRvTimer = null;
    }
  }

  // Keep --footer-h in sync with the real footer height, and reserve that height at the
  // page bottom (on <body>) so the last section clears the fixed checkout bar — wherever
  // it falls in the section order (e.g. lp-about renders after this section).
  observeFooterHeight() {
    const footer = this.querySelector('.lpv2-products__footer');
    if (!footer) return;
    const set = () => {
      const h = footer.offsetHeight;
      this.style.setProperty('--footer-h', h + 'px');
      document.body.style.paddingBottom = h + 'px';
    };
    set();
    if ('ResizeObserver' in window) {
      this.footerObserver = new ResizeObserver(set);
      this.footerObserver.observe(footer);
    } else {
      window.addEventListener('resize', set);
    }
  }

  // Perf: pause the cards' + finders' decorative infinite animations while they're off-screen.
  // One shared IntersectionObserver toggles `.in-view`; the CSS pauses the glows/sheens for
  // anything without it. A single observer over all cards is cheap — intersection is
  // compositor-computed and the callback fires only on crossings — and it saves far more paint
  // than it costs. The 200px rootMargin resumes animations just before a card scrolls in, so
  // nothing visibly pops. Cards moved by the finder re-sort stay observed (observation persists
  // across DOM moves within the document).
  observeCardVisibility() {
    const cards = this.querySelectorAll('.lpv2-card, .lpv2-up, .lpv2-finder');
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) {
      cards.forEach((c) => c.classList.add('in-view')); // no IO support → leave effects on
      return;
    }
    this._cardVisObserver = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.target.classList.toggle('in-view', e.isIntersecting)),
      { rootMargin: '200px 0px' },
    );
    cards.forEach((c) => this._cardVisObserver.observe(c));
  }

  // Map the finder's recommended set to [{ variantId, title }] by looking up each
  // product's name from the placed cards (the finder payload carries shade copy, not the
  // name). Drives the additive `_<id>_recommended_json` cart attribute.
  buildRecommendedFull(recommended) {
    const titleByVid = {};
    this.querySelectorAll('lp-bundle-item').forEach((el) => {
      titleByVid[Number(el.getAttribute('variant-id'))] = el.getAttribute('product-title') || '';
    });
    return (recommended || [])
      .map((r) => Number(r.variantId))
      .filter(Boolean)
      .map((vid) => ({ variantId: vid, title: titleByVid[vid] || '' }));
  }

  // Finder completion. Surfaces the matches with the "shade match" register and re-sorts the
  // stack around the finder (see reorderStack). No auto-select — the customer keeps whatever
  // they picked. The run is also stored for the hidden cart attributes (→ order → Flow).
  applyFinderResult(detail) {
    const recommended = (detail && detail.recommended) || [];
    // finderId namespaces the cart-attribute keys; finderRecommended is the ordered match set.
    this.finderId = (detail && detail.finderId) || 'lp_shade';
    this.finderTraits = (detail && detail.answers) || null;
    const recIds = recommended.map((r) => Number(r.variantId)).filter(Boolean);
    this.finderRecommended = recIds;
    // Full ordered set with product NAMES, for the JSON cart attribute (merchant-readable).
    // The finder payload only carries per-shade copy (r.text), not the product name — look
    // the name up from the placed cards by variant id.
    this.finderRecommendedFull = this.buildRecommendedFull(recommended);
    if (!recIds.length) return;

    // Ensure the default snapshot exists (desc + selection) so Start-over can restore the
    // original card copy after we overwrite it below.
    this.captureCardHomes();

    // Reset every card to its default description first, so re-running the finder (change an
    // answer → "See my kit" again without a Start-over) never leaves a previously-matched
    // card showing stale copy.
    (this.cardHomes || []).forEach((h) => {
      if (h.desc == null) return;
      const d = h.el.querySelector('.lpv2-card__desc');
      if (d) d.innerHTML = h.desc;
    });

    // Per-shade copy keyed by variant id, from the outcomes/metaobject JSON (r.text). The
    // finder associates bespoke copy with the customer's answers; matched cards swap their
    // default description for it. Blank text leaves the card's default description in place.
    const textById = {};
    recommended.forEach((r) => {
      if (r && r.text) textById[Number(r.variantId)] = r.text;
    });

    const matchSet = new Set(recIds);
    // Reveal the "shade match" register on matched cards (+ tag them so the grid gives their
    // register tab the same top clearance as the other register tabs); hide it on the rest.
    // Matched cards also adopt their bespoke shade copy (if any) in place of the default.
    this.querySelectorAll('lp-bundle-item').forEach((item) => {
      const matched = matchSet.has(Number(item.getAttribute('variant-id')));
      const reg = item.querySelector('[data-card-match-reg]');
      if (reg) reg.hidden = !matched;
      item.classList.toggle('lpv2-card--has-match', matched);
      if (matched) this.setCardText(item, textById[Number(item.getAttribute('variant-id'))]);
    });

    // Re-sort the stack around the finder.
    this.reorderStack(matchSet);

    // Consolidate the result onto the PRIMARY (first) finder. With multiple shade-assistant
    // entry points, the page always scrolls back to the first card and the matched stack
    // reorders beneath it — so the first card must show the done summary no matter which
    // card the customer ran. Any other instance (e.g. the one they actually used) returns to
    // its intro. Done unconditionally by position, so we never need to know which one ran.
    this.consolidateFinderResult();

    // Hide any extra Shade Assistant entry points flagged "hide after result" — with several
    // finders the leftover intro cards look cluttered once the stack regroups. The slot showing
    // the consolidated result is always kept (see hideMarkedFinderSlots).
    this.hideMarkedFinderSlots();

    // Land the customer with the finder centred, kept picks above + matches below.
    this.scrollToFinderCenter();
  }

  // Hide finder slots flagged `data-hide-after-result` (the block toggle), EXCEPT the one that
  // holds the consolidated result (the first finder in the document). Keeps a single clean result
  // card when a page has multiple Shade Assistant entry points. Reversed by showMarkedFinderSlots.
  hideMarkedFinderSlots() {
    const primaryFinder = document.querySelector('lp-product-finder');
    const primarySlot = primaryFinder && primaryFinder.closest('.lpv2-products__finder-slot');
    this.querySelectorAll('.lpv2-products__finder-slot[data-hide-after-result]').forEach((slot) => {
      if (slot === primarySlot) return; // never hide the slot showing the result
      slot.hidden = true;
    });
  }

  // Bring the flagged finder slots back on Start-over, so the default layout is intact again.
  showMarkedFinderSlots() {
    this.querySelectorAll('.lpv2-products__finder-slot[data-hide-after-result]').forEach((slot) => {
      slot.hidden = false;
    });
  }

  // Force the first finder into its done/result state and reset every other finder to intro,
  // so the single visible result always lives on the primary card (see applyFinderResult).
  consolidateFinderResult() {
    const finders = document.querySelectorAll('lp-product-finder');
    if (finders.length < 2) return; // single entry point: it already shows its own result
    finders.forEach((f, i) => {
      if (i === 0) {
        if (typeof f.presentDone === 'function') f.presentDone();
      } else if (typeof f.toIntro === 'function') {
        f.toIntro();
      }
    });
  }

  // Re-sort the main product grid after the quiz completes, into:
  //   1. selected shades that are NOT matches   (the customer's own picks — kept, up top)
  //   2. the SHADE FINDER card
  //   3. matched shades that are ALSO selected
  //   4. matched shades that are not selected
  //   5. upsells (gifts / paid add-ons)
  //   6. any other non-shade items
  //   7. everything else (unselected + unmatched shades — the "other products")
  // Marketing cards aren't a fixed group — each is spliced into the result at its own
  // configured % position (block setting → data-reposition-percent).
  // Within each group the existing DOM order is preserved. The re-sort is deterministic
  // (keyed on selection + match, not position), so re-running from an already-sorted stack
  // is safe. Custom elements guard reconnection (`_wired`), so appendChild-moving is safe.
  reorderStack(matchSet) {
    const grid = this.querySelector('[data-main-grid]');
    const slot = this.querySelector('.lpv2-products__finder-slot');
    if (!grid || !slot) return;
    const above = [];
    const matchedSel = [];
    const matchedUn = [];
    const upsells = [];
    const marketing = []; // { el, pct } — positioned by % through the stack, not grouped
    const others = [];
    const rest = [];
    Array.from(grid.children).forEach((el) => {
      if (el === slot) return; // the finder is placed explicitly, between 1 and 3
      if (!el.matches('lp-bundle-item')) {
        if (el.matches('.lpv2-products__upsell')) upsells.push(el);
        else if (el.matches('lp-marketing-card')) {
          // Per-card position (block setting → data-reposition-percent), clamped to 0–100.
          const pct = Math.max(0, Math.min(100, Number(el.getAttribute('data-reposition-percent'))));
          marketing.push({ el, pct: Number.isFinite(pct) ? pct : 50 });
        } else others.push(el);
        return;
      }
      const recommended = matchSet.has(Number(el.getAttribute('variant-id')));
      const input = el.querySelector('input');
      const selected = !!(input && input.checked);
      if (recommended && selected) matchedSel.push(el);
      else if (recommended) matchedUn.push(el);
      else if (selected) above.push(el);
      else rest.push(el);
    });
    // Base stack without the marketing cards; each marketing card is then spliced in at its
    // configured % through this base (so 75% ≈ three-quarters of the way down). Insert in
    // ascending target order and offset by the number already inserted (k) — each prior splice
    // shifts later indices by one.
    const stack = [...above, slot, ...matchedSel, ...matchedUn, ...upsells, ...others, ...rest];
    marketing
      .map(({ el, pct }) => ({ el, at: Math.round((pct / 100) * stack.length) }))
      .sort((a, b) => a.at - b.at)
      .forEach((ins, k) => stack.splice(Math.min(stack.length, ins.at + k), 0, ins.el));
    stack.forEach((el) => grid.appendChild(el));
  }

  // Scroll so the finder's done card sits vertically centred in the space above the fixed
  // checkout footer — the kept picks read above it, the matches below. Deferred a frame so
  // the re-sort/reflow settles before measuring.
  scrollToFinderCenter() {
    const slot = this.querySelector('.lpv2-products__finder-slot');
    if (!slot) return;
    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => {
      const offset = window.pageYOffset || document.documentElement.scrollTop || 0;
      const rect = slot.getBoundingClientRect();
      const footer = this.querySelector('.lpv2-products__footer');
      const footerH = footer ? footer.getBoundingClientRect().height : 0;
      const avail = window.innerHeight - footerH;
      // Centre the card in the available space, but never push its top above a 12px margin.
      const topInView = Math.max(12, (avail - rect.height) / 2);
      const target = rect.top + offset - topInView;
      const max = Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
      window.scrollTo({
        top: Math.max(0, Math.min(target, max)),
        behavior: reduce ? 'auto' : 'smooth',
      });
    });
  }

  // Glide to the first matched shade card (the first with its "shade match" register
  // revealed). Triggered by the buy-now guard's "back to your matches" CTA.
  scrollToFinderResult() {
    const firstMatch = Array.from(this.querySelectorAll('lp-bundle-item')).find((item) => {
      const reg = item.querySelector('[data-card-match-reg]');
      return reg && !reg.hidden;
    });
    if (!firstMatch) return;
    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Defer to the next frame so any pending reflow settles before we measure. A
    // same-tick smooth scroll can target a pre-reflow position and overshoot —
    // cutting off the top — especially on iOS Safari, whose collapsing toolbar
    // shifts the layout mid-scroll. Land a little above the card (clear its register tab).
    requestAnimationFrame(() => {
      const offset = window.pageYOffset || document.documentElement.scrollTop || 0;
      const top = firstMatch.getBoundingClientRect().top + offset - 44;
      const max = Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
      window.scrollTo({
        top: Math.max(0, Math.min(top, max)),
        behavior: reduce ? 'auto' : 'smooth',
      });
    });
  }

  // Re-start (finder reset): hide the shade-match registers + drop the match tags. The
  // re-sorted stack is left as-is (the finder stays put and just reopens; the customer's
  // own selection is untouched) — the next completion re-sorts deterministically.
  restoreDefaultLayout() {
    this.querySelectorAll('[data-card-match-reg]').forEach((reg) => {
      reg.hidden = true;
    });
    this.querySelectorAll('.lpv2-card--has-match').forEach((el) =>
      el.classList.remove('lpv2-card--has-match'),
    );
    // Restore each card's default description (matched cards had it swapped for bespoke copy).
    (this.cardHomes || []).forEach((h) => {
      if (h.desc == null) return;
      const d = h.el.querySelector('.lpv2-card__desc');
      if (d) d.innerHTML = h.desc;
    });
    // Bring back any Shade Assistant cards hidden after the result.
    this.showMarkedFinderSlots();
    this.finderRecommended = null;
  }
}

// A promo card in the stack (e.g. offer video). Not selectable — just wires the
// tap-to-play control via the shared helper.
class LpMarketingCard extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    const video = this.querySelector('.lpv2-mkt__video');
    if (!video) return;
    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      // Reduced motion: no autoplay. The play button (revealed via CSS) is the manual
      // control; it stays muted so there's never audio.
      wireCardVideo(this, '.lpv2-mkt__video', { keepMuted: true });
      return;
    }
    // Default: a silent looping promo that autoplays while in view (paused off-screen to
    // save bandwidth). No audio, no button.
    video.muted = true;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 },
    );
    io.observe(this);
  }
}

// Define lp-cart first so it's listening before the items upgrade and dispatch
// their initial (pre-selected) lp-cart:change events.
if (!customElements.get('lp-cart')) customElements.define('lp-cart', LpCart);
if (!customElements.get('lp-bundle-item')) customElements.define('lp-bundle-item', LpBundleItem);
if (!customElements.get('lp-upsell-item')) customElements.define('lp-upsell-item', LpUpsellItem);
if (!customElements.get('lp-marketing-card')) customElements.define('lp-marketing-card', LpMarketingCard);
